import { ApiService } from "./ApiService.js";
import { ServiceConsts } from "./ServiceConsts.js";
import { base64ToBlob, getURLFileName } from "$/utils/upload";

export class DocumentService {

    static async uploadPrivatePDF(file, tenantId, onProgress) {
        try {
            const totalSize = file.size; // get the total size of the file
            let loadedSize = 0; // track the loaded size

            return new Promise(async (resolve, reject) => {
                const xhr = new XMLHttpRequest();

                const token = await ApiService.getToken();

                xhr.open("POST", `${ServiceConsts.API_URL}/tenants/${tenantId}/documents/`);
                xhr.setRequestHeader("Authorization", `Bearer ${token}`);

                xhr.upload.addEventListener("progress", (event) => {
                    if (event.lengthComputable) {
                        loadedSize = event.loaded; // update the loaded size
                        const percentage = (loadedSize / totalSize) * 100; // calculate the percentage
                        if (onProgress) {
                            onProgress(percentage); // invoke the progress callback with the percentage
                        }
                    }
                });

                xhr.onreadystatechange = () => {
                    if (xhr.readyState === XMLHttpRequest.DONE) {
                        if (xhr.status === 200) {
                            resolve(JSON.parse(xhr.responseText));
                        } else {
                            reject({isError: true, error: xhr.responseText});
                        }
                    }
                };

                const formData = new FormData();
                formData.append('file', file);

                xhr.send(formData);
            });
        }

        catch (error) {
            console.error("Error uploading private document:", error);
            throw error;
        }
    }

    static async getPrivateDocument(tenantId, documentId) {
        try {
            const token = await ApiService.getToken();
            return await fetch(`${ServiceConsts.API_URL}/tenants/${tenantId}/documents/${documentId}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
        } catch (error) {
            console.error("Error fetching private document:", error);
            throw error;
        }
    }

    static async getPrivateDocumentData(tenantId, documentId) {
        try {
            return await ApiService.get(`${ServiceConsts.API_URL}/tenants/${tenantId}/documents/${documentId}/data`)
        }
        catch (error) {
            console.error("Error fetching private document data:", error);
            throw error;
        }
    }

    static async getRecentDocuments(tenantId) {
        try {
            return ApiService.get(ServiceConsts.API_URL + `/tenants/${tenantId}/documents/recent`);
        } catch (error) {
            console.error("Error fetching recent documents:", error);
            throw error;
        }
    }

    static async checkPDFExistence(pdfUrl) {
        const token = await ApiService.getToken();
        let fileName = getURLFileName(pdfUrl);
        let response = await fetch(
            `${ServiceConsts.API_URL}/documents/public/web/existence/${btoa(pdfUrl)}/${fileName}`,
            { method: "GET", headers: { Authorization: `Bearer ${token}` } },
        );
        return response.ok;
    }

    static async uploadPublicPDF(file, source) {
        try {
            const token = await ApiService.getToken();

            let formData = new FormData();
            const convertedFile = new File(
                [base64ToBlob(file, "application/pdf")],
                getURLFileName(source),
                { type: "application/pdf" },
            );
            formData.append("file", convertedFile);
            formData.append("source", source);

            return await fetch(
                `${ServiceConsts.API_URL}/documents/public/web/upload`,
                {
                    method: "POST",
                    body: formData,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
        } catch (error) {
            console.error("Error uploading public document:", error);
        }
    }

    static async countDocumentSnippets(documentId) {
        try {
            return await ApiService.get(`${ServiceConsts.API_URL}/documents/${documentId}/count`);
        } catch (error) {
            console.error("Error counting document snippets:", error);
        }
    }
    
    static async deleteDocument(tenantId, documentId) {
        try {
            return await ApiService.delete(`${ServiceConsts.API_URL}/tenants/${tenantId}/documents/${documentId}`);
        } catch (error) {
            console.error("Error deleting document:", error);
        }
    }
}
